import React, { useState, useEffect } from 'react';
import { BarChart3, Check, ExternalLink, AlertCircle, Loader2, Unlink, RefreshCw } from 'lucide-react';
import { useBusiness } from '../contexts/BusinessContext';
import { supabase } from '../lib/supabaseClient';
import toast from 'react-hot-toast';

interface GoogleAdsConnectButtonProps {
  size?: 'small' | 'medium' | 'large';
  className?: string;
}

interface GoogleAdsConnection {
  id: string;
  customer_id: string;
  customer_name: string | null;
  is_active: boolean;
  last_synced_at: string | null;
}

export default function GoogleAdsConnectButton({ size = 'medium', className = '' }: GoogleAdsConnectButtonProps) {
  const { selectedBusiness: business } = useBusiness();
  const [loading, setLoading] = useState(false);
  const [connection, setConnection] = useState<GoogleAdsConnection | null>(null);
  const [checkingConnection, setCheckingConnection] = useState(true);

  // Check for existing connection
  useEffect(() => {
    checkConnection();
    
    // Check URL for OAuth callback
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('google_ads') === 'connected') {
      // Wait a moment then refresh
      setTimeout(() => {
        checkConnection();
        // Clean up URL
        window.history.replaceState({}, '', window.location.pathname);
      }, 2000);
    } else if (urlParams.get('google_ads') === 'error') {
      // Handle OAuth error
      const error = urlParams.get('error');
      const errorDescription = urlParams.get('error_description');
      
      let errorMessage = 'Failed to connect Google Ads.';
      if (errorDescription) {
        if (errorDescription.includes('developer token is not valid')) {
          errorMessage = 'Developer Token is not valid. Please wait for Google to approve your Developer Token, or check if it\'s correctly configured in Supabase secrets.';
        } else {
          errorMessage = errorDescription;
        }
      }
      
      toast.error(errorMessage);
      
      // Clean up URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [business?.id]);

  const checkConnection = async () => {
    if (!business?.id) {
      setCheckingConnection(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('google_ads_connections')
        .select('id, customer_id, customer_name, is_active, last_synced_at')
        .eq('business_id', business.id)
        .eq('is_active', true)
        .maybeSingle();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error checking Google Ads connection:', error);
      }

      setConnection(data || null);
    } catch (error) {
      console.error('Error checking connection:', error);
    } finally {
      setCheckingConnection(false);
    }
  };

  const handleConnect = async () => {
    if (!business?.id) return;

    setLoading(true);
    try {
      // Call edge function to get OAuth URL
      const { data, error } = await supabase.functions.invoke('initiate-google-ads-oauth', {
        body: {
          businessId: business.id,
          returnUrl: `${window.location.origin}/marketing?google_ads=connected`,
        },
      });

      if (error || !data?.authUrl) {
        console.error('Error getting OAuth URL:', error);
        toast.error('Failed to connect to Google Ads. Please try again.');
        return;
      }

      // Redirect to Google OAuth
      window.location.href = data.authUrl;

    } catch (error) {
      console.error('Error connecting to Google Ads:', error);
      toast.error('Failed to connect to Google Ads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFetchData = async () => {
    if (!business?.id || !connection) return;

    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('fetch-google-ads-data', {
        body: {
          businessId: business.id,
          forceRefresh: true,
        },
      });

      if (error) {
        console.error('Error fetching data:', error);
        // Try to extract error message from response
        let errorMessage = 'Failed to fetch Google Ads data. Please try again.';
        if (error.message) {
          errorMessage = error.message;
        } else if (error.context?.body) {
          try {
            const errorBody = typeof error.context.body === 'string' 
              ? JSON.parse(error.context.body) 
              : error.context.body;
            if (errorBody.error) {
              errorMessage = errorBody.error;
            }
          } catch (e) {
            // Ignore parse errors
          }
        }
        toast.error(errorMessage);
        return;
      }

      if (data?.error) {
        if (data.error.includes('Customer ID not available') || data.error.includes('reconnect')) {
          toast.error(data.error);
        } else if (data.error.includes('Developer Token')) {
          toast.error('Developer Token not configured. Please check Supabase secrets.');
        } else {
          toast.error(data.error);
        }
        return;
      }

      if (data?.campaigns) {
        toast.success(`Successfully fetched ${data.campaigns.length} campaigns!`);
      } else {
        toast.success('Successfully updated connection!');
      }
      // Refresh connection to update customer_id and last_synced_at
      checkConnection();
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to fetch Google Ads data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!business?.id || !connection) return;

    if (!confirm('Are you sure you want to disconnect your Google Ads account? This will stop syncing campaign data.')) {
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('google_ads_connections')
        .update({ is_active: false })
        .eq('id', connection.id);

      if (error) {
        console.error('Failed to disconnect:', error);
        toast.error('Failed to disconnect Google Ads. Please try again.');
        return;
      }

      toast.success('Google Ads account disconnected successfully!');
      setConnection(null);
    } catch (error) {
      console.error('Error disconnecting:', error);
      toast.error('Failed to disconnect Google Ads. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (checkingConnection) {
    return (
      <div className={`flex items-center justify-center ${className}`}>
        <Loader2 className="h-4 w-4 animate-spin text-gray-400" />
      </div>
    );
  }

  const sizeClasses = {
    small: 'text-xs px-3 py-1.5',
    medium: 'text-sm px-4 py-2',
    large: 'text-base px-6 py-3',
  };

  if (connection) {
    return (
      <div className={`space-y-3 ${className}`}>
        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
          <div className="flex items-center gap-2">
            <Check className="h-4 w-4 text-green-400" />
            <span className="text-sm text-green-400 font-medium">Google Ads Connected</span>
          </div>
          <p className="text-xs text-gray-400 mt-1">
            Account: {connection.customer_name || connection.customer_id}
          </p>
          {connection.last_synced_at && (
            <p className="text-xs text-gray-500 mt-1">
              Last synced: {new Date(connection.last_synced_at).toLocaleDateString()}
            </p>
          )}
        </div>

        <div className="flex gap-2">
          <button
            onClick={handleFetchData}
            disabled={loading}
            className={`flex-1 flex items-center justify-center gap-2 ${sizeClasses[size]} bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
            title={connection.customer_id === 'pending' ? 'Will fetch customer ID first, then campaign data' : 'Fetch campaign data'}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <RefreshCw className="h-4 w-4" />
                Fetch Data
              </>
            )}
          </button>
          <button
            onClick={handleDisconnect}
            disabled={loading}
            className={`flex-1 flex items-center justify-center gap-2 ${sizeClasses[size]} bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
          >
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <>
                <Unlink className="h-4 w-4" />
                Disconnect
              </>
            )}
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={handleConnect}
      disabled={loading}
      className={`w-full flex items-center justify-center gap-2 ${sizeClasses[size]} bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${className}`}
    >
      {loading ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <>
          <BarChart3 className="h-4 w-4" />
          Connect Google Ads
          <ExternalLink className="h-3 w-3" />
        </>
      )}
    </button>
  );
}

